import { ChangeEvent } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Box, Flex, FormControl, HStack, Icon, Text } from '@chakra-ui/react'
import {
  BxsHelpCircle,
  Checkbox,
  FormErrorMessage,
  NumberInput,
  SingleSelect,
  TouchableTooltip,
} from '@opengovsg/design-system-react'

import { DISPLAY_TIME_UNIT_OPTIONS } from '~shared/utils'

import { BookingRulesFields } from '../types/field-info'
import { validateMaxLeadTimeMinutes } from '../utils/form-utils'

export const MaxLeadTimeCheckbox = () => {
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
    register,
  } = useFormContext<BookingRulesFields>()
  // useWatch ensures that this component rerenders when isMaxLeadTimeEnabled
  // changes, else disabled and error states go out of sync
  const isMaxLeadTimeEnabled = useWatch<BookingRulesFields>({
    control,
    name: 'isMaxLeadTimeEnabled',
  }) as boolean
  return (
    <Flex flexDir={'column'} alignItems={'flex-start'}>
      {/* We wrap the Checkbox in a HStack to prevent it from stretching to full width */}
      <HStack>
        <Checkbox
          size="sm"
          {...register('isMaxLeadTimeEnabled', {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              if (!event.target.checked) {
                setValue('maxLeadTimeQuantity', null)
                clearErrors('maxLeadTimeQuantity')
              }
            },
          })}
        >
          <Text textStyle={'subhead-2'}>Release slots on a rolling basis</Text>
        </Checkbox>
        {/* Wrap tooltip in a Box with padding to force alignment with label */}
        <Box>
          <TouchableTooltip
            label="e.g. if this is 10 days, then on 1st Jan, slots are only shown until 11th Jan. On 2nd Jan, slots are shown until 12th Jan, and so on."
            placement={'top'}
          >
            {/* We use display block to prevent the icon from moving itself upwards and getting misaligned */}
            {/* The click handler prevents activating the default behaviour of toggling the checkbox */}
            <Icon
              onClick={(evt) => evt.preventDefault()}
              as={BxsHelpCircle}
              fontSize="1rem"
              display="block"
              aria-hidden
            />
          </TouchableTooltip>
        </Box>
      </HStack>
      <Text
        textStyle={'body-2'}
        as="span"
        ml="36px"
        mt={1.5}
        pb={2}
        color={
          isMaxLeadTimeEnabled
            ? undefined
            : 'interaction.support.disabled-content'
        }
      >
        Only show a moving window of available slots over...
      </Text>
      <FormControl isInvalid={!!errors.maxLeadTimeQuantity}>
        <Flex flexWrap={'wrap'} alignItems="center" gap="4px" ml="36px">
          <Controller
            name="maxLeadTimeQuantity"
            rules={{
              validate: validateMaxLeadTimeMinutes,
            }}
            render={({ field: { onChange, ref, value } }) => (
              <NumberInput
                ref={ref}
                value={(value ?? '') as string}
                onChange={(_valueAsString, valueAsNumber) =>
                  onChange(isNaN(valueAsNumber) ? null : valueAsNumber)
                }
                showSteppers={false}
                width="20%"
                minW={{ base: '100%', md: '80px' }}
                maxW={{ md: '10ch' }}
                display="inline-block"
                fontSize={'14px'}
                isDisabled={!isMaxLeadTimeEnabled}
                size="sm"
              />
            )}
          />
          <Controller
            name="maxLeadTimeUnit"
            render={({ field: { onChange, ref, value } }) => (
              // Force select box to be smaller, but big enough to accommodate all characters
              <Box
                maxW={{ md: '15ch' }}
                minW={{ base: '100%', md: 'unset' }}
                display="inline-block"
              >
                <SingleSelect
                  ref={ref}
                  name="maxLeadTimeUnit"
                  isClearable={false}
                  value={(value ?? '') as string}
                  onChange={onChange}
                  size="sm"
                  items={DISPLAY_TIME_UNIT_OPTIONS}
                  isDisabled={!isMaxLeadTimeEnabled}
                />
              </Box>
            )}
          />
          <Text
            textStyle={'body-2'}
            as="span"
            color={
              isMaxLeadTimeEnabled
                ? undefined
                : 'interaction.support.disabled-content'
            }
          >
            from the current time
          </Text>
        </Flex>
        <FormErrorMessage ml="36px">
          {errors.maxLeadTimeQuantity?.message}
        </FormErrorMessage>
      </FormControl>
    </Flex>
  )
}
